import {useState} from "react";
import {App as AntdApp, Tag} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetReportScheduleQuery,
	useUpdateReportScheduleMutation,
	useSendReportNowMutation,
} from "~/reduxs/api/projectApiSlice";
import ReportScheduleForm from "./ReportScheduleForm";
import style from "../style/Project.module.scss";

const cn = className.bind(style);

const SCHEDULE_LABEL = {
	off: 'Tắt',
	daily: 'Hằng ngày',
	weekly: 'Hằng tuần',
	monthly: 'Hằng tháng',
};

/**
 * Card "Báo cáo email định kỳ" (#22 Reporting) trên dashboard dự án: hiện lịch đang bật +
 * mốc gửi kế tiếp, nút đổi lịch (modal) và nút "Gửi ngay" để nhận thử 1 báo cáo. Gói không
 * có kênh email (`allowed=false` từ BE) → gợi ý nâng cấp thay vì nút cấu hình.
 */
function ReportScheduleCard({projectId}) {

	const {notification} = AntdApp.useApp();

	const {data, isLoading} = useGetReportScheduleQuery(projectId);
	const [updateSchedule, {isLoading: saving}] = useUpdateReportScheduleMutation();
	const [sendNow, {isLoading: sending}] = useSendReportNowMutation();

	const [open, setOpen] = useState(false);

	if (isLoading || !data) return null;

	const {schedule = 'off', next, allowed, mail_ready: mailReady} = data;

	// Hạ tầng email chưa được cấu hình (MAIL_* trống) → ẩn hẳn chức năng, không chìa
	// nút "Bật lịch"/"Gửi ngay" chết ra cho user.
	if (mailReady === false) return null;

	const handleSave = async (value) => {
		try {
			await updateSchedule({id: projectId, schedule: value}).unwrap();
			notification.success({
				message: 'Đã lưu',
				description: value === 'off'
					? 'Đã tắt báo cáo email định kỳ.'
					: `Sẽ gửi báo cáo ${SCHEDULE_LABEL[value]?.toLowerCase()} vào email của bạn.`,
			});
			setOpen(false);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không thể lưu lịch báo cáo')});
		}
	};

	const handleSendNow = async () => {
		try {
			const res = await sendNow(projectId).unwrap();
			notification.success({message: 'Đã gửi', description: res?.message || 'Báo cáo đã vào hàng đợi email.'});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không thể gửi báo cáo')});
		}
	};

	return (
		<div className={cn('card')} style={{marginTop: 16}}>
			<div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap'}}>
				<div>
					<h3 style={{fontSize: 15, fontWeight: 600, margin: '0 0 4px'}}>
						<FontAwesomeIcon icon="fa-light fa-envelope-open-text" /> Báo cáo email định kỳ
					</h3>
					<span style={{fontSize: 13, color: '#6b7280'}}>
						{!allowed ? (
							'Báo cáo qua email thuộc gói Cơ bản trở lên — nâng cấp gói để nhận tổng hợp SEO định kỳ.'
						) : schedule === 'off' ? (
							'Chưa bật — tổng hợp điểm crawl, Search Console, Analytics, PageSpeed gửi thẳng vào hộp thư.'
						) : (
							<>
								<Tag color="blue">{SCHEDULE_LABEL[schedule] || schedule}</Tag>
								{next && <span>Lần gửi kế tiếp: <strong>{next}</strong></span>}
							</>
						)}
					</span>
				</div>
				{!allowed ? (
					<Button to="/subscription" leftIcon={<FontAwesomeIcon icon="fa-light fa-arrow-up-right-dots" />}>
						Nâng cấp gói
					</Button>
				) : (
					<div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
						<Button white loading={sending} onClick={handleSendNow}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-paper-plane" />}>
							Gửi ngay
						</Button>
						<Button onClick={() => setOpen(true)}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-calendar-clock" />}>
							{schedule === 'off' ? 'Bật lịch' : 'Đổi lịch'}
						</Button>
					</div>
				)}
			</div>

			<ReportScheduleForm
				open={open}
				submitting={saving}
				current={schedule}
				onCancel={() => setOpen(false)}
				onSubmit={handleSave}
			/>
		</div>
	);
}

export default ReportScheduleCard;
